import {
  AERIE_GATE_REACH,
  AerieAllyActEventSchema,
  AerieAllyEventSchema,
  AerieEndedEventSchema,
  AerieGateEventSchema,
  type AerieMode,
  AerieRejectedSchema,
  AerieStationsEventSchema,
  type AssetManifest,
  type BossTelegraphEvent,
  gateInto,
  MobIdSchema,
  MOBS,
  ringDistance,
  SHEFIRA_ZONE_ID,
  ShefiraDiveEventSchema,
  ShefiraFlightEventSchema,
  ShefiraRingEventSchema,
  type ShefiraStateEvent,
  ShefiraStateEventSchema,
  StationRejectedSchema,
  type StationState,
  StationUsedEventSchema,
  STORY_ALLIES,
  TILE_SIZE,
  type TilePosition,
  type ZoneDefinition,
  type ZoneGate,
} from '@shefira/shared';
import type Phaser from 'phaser';

import { cue, cueAt } from '../../audio/cues.js';
import { language } from '../../i18n/language.js';
import { AerieGatePanel } from '../../ui/aerie-gate-panel.js';
import { AerieResult } from '../../ui/aerie-result.js';
import type { AerieBarInfo } from '../../ui/aerie-text.js';
import type { BossBarState } from '../../ui/boss-bar.js';
import { uiFormat, uiText } from '../../ui/text.js';
import type { ShakeKind } from '../screen-shake.js';
import { FireRing } from './fire-ring.js';
import type { Point } from './shefira-motion.js';
import { ShefiraView } from './shefira-view.js';
import { StationLayer } from './station-layer.js';
import { inStationReach, STATION_NAMES, STATION_REFUSALS, stationApproach } from './stations.js';

/**
 * The client's side of Shefira's Aire (ADR 0068, ADR 0071): in the world,
 * the crevasse's window; in the Aire, her body on its large canvas, her
 * telegraphs of her own (the dive's shadow, the ring of fire), the Company's
 * posts, the story's allies' last words and the end of the fight. Only what
 * the server said is shown, and only intents are sent: `aerie_status`,
 * `aerie_enter`, `use_station`. The scene lends it what it knows of the
 * entities it draws.
 */

interface AerieRoom {
  sessionId: string;
  send: (type: string, payload?: unknown) => void;
  onMessage: (type: string, callback: (payload: unknown) => void) => unknown;
}

/** One entity of the room as the scene holds it. */
interface MobEntry {
  mobId: string;
  hp: number;
  maxHp: number;
}

export interface AerieClientDeps {
  scene: Phaser.Scene;
  manifest: AssetManifest;
  zone: ZoneDefinition;
  room: AerieRoom;
  isOpen: (tile: TilePosition) => boolean;
  /** Where the server says the local player stands; undefined while fallen or unknown. */
  me: () => TilePosition | undefined;
  /** Where the scene draws an entity (`mob:<id>`, a session id), feet in world pixels. */
  drawnAt: (key: string) => Point | undefined;
  /** The scene's view of an entity, for Shefira's stand-in body and the allies' speech. */
  viewOf: (
    key: string,
  ) => { container: Phaser.GameObjects.Container; say: (text: string) => void } | undefined;
  mob: (id: string) => MobEntry | undefined;
  nameOf: (sessionId: string) => string | undefined;
  /** Walk to a tile (the move marker shown); false when there is no way. */
  walkTo: (tile: TilePosition) => boolean;
  notice: (text: string) => void;
  floatText: (key: string, text: string, colour: number, ms: number) => void;
  jolt: (kind: ShakeKind) => void;
  listener: () => Point | undefined;
}

/** A fixed phrase in the world's bitmap font: French keeps its accents, English is looked up. */
function worldText(french: string): string {
  return language() === 'en' ? uiText(french) : french;
}

const PINNED_COLOUR = 0xffe08a;
const DIVE_COLOUR = 0xff5c5c;
const FROST_COLOUR = 0xbfe3ff;
const SEAL_COLOUR = 0xf0dc9a;
const ROOTS_COLOUR = 0x8cf07a;

export class AerieClient {
  private readonly inAerie: boolean;
  private readonly gate: ZoneGate | undefined;
  private state: ShefiraStateEvent | null = null;
  /** Scene time the state came: the landing's countdown runs from it. */
  private stateAt = 0;
  private view?: ShefiraView;
  private stations?: StationLayer;
  private ring?: FireRing;
  private result?: AerieResult;
  private gatePanel?: AerieGatePanel;
  private ended = false;
  private visiting: { kind: 'station'; station: StationState } | { kind: 'gate' } | undefined;

  constructor(private readonly deps: AerieClientDeps) {
    const { zone, scene, manifest } = deps;
    this.inAerie = zone.id === SHEFIRA_ZONE_ID;
    this.gate = gateInto(zone, SHEFIRA_ZONE_ID);
    if (this.inAerie) {
      this.view = new ShefiraView(scene, manifest);
      const small = manifest.sheets['font.pixel-small'];
      this.stations = new StationLayer(
        scene,
        zone.props,
        small === undefined ? undefined : { atlasKey: small.atlas, fontKey: small.prefix },
      );
      this.ring = new FireRing(scene, deps.isOpen);
      this.result = new AerieResult();
    }
    if (this.gate !== undefined) {
      this.gatePanel = new AerieGatePanel((mode) => {
        this.enter(mode);
      });
    }
    this.listen();
  }

  private get now(): number {
    return this.deps.scene.time.now;
  }

  private listen(): void {
    const { room } = this.deps;
    room.onMessage('aerie_gate', (payload) => {
      const parsed = AerieGateEventSchema.safeParse(payload);
      if (parsed.success) this.gatePanel?.setGate(parsed.data);
    });
    room.onMessage('aerie_rejected', (payload) => {
      const parsed = AerieRejectedSchema.safeParse(payload);
      if (!parsed.success) return;
      cue('sfx.error');
      if (this.gatePanel?.isOpen === true) {
        this.gatePanel.showRejected(parsed.data);
        // What changed since, the window shows again.
        room.send('aerie_status', {});
      } else {
        this.deps.notice(uiText('La crevasse reste close.'));
      }
    });
    if (!this.inAerie) return;
    room.onMessage('shefira_state', (payload) => {
      const parsed = ShefiraStateEventSchema.safeParse(payload);
      if (parsed.success) this.onState(parsed.data);
    });
    room.onMessage('shefira_flight', (payload) => {
      const parsed = ShefiraFlightEventSchema.safeParse(payload);
      if (!parsed.success) return;
      const { state, ms, cause } = parsed.data;
      const key = `mob:${parsed.data.mobId}`;
      if (state === 'takeoff') {
        this.view?.takeoff(ms, this.now);
        this.cueNear('sfx.dragon-wings', key);
        this.deps.jolt('rumble');
        return;
      }
      this.view?.landing(ms, this.now);
      this.cueNear('sfx.dragon-wings', key);
      this.deps.scene.time.delayedCall(Math.max(0, ms - 400), () => {
        this.deps.jolt('landing');
        this.cueNear('sfx.dragon-rumble', key);
      });
      if (cause === 'harpooned') {
        this.deps.notice(uiText('Les harpons la clouent au sol : frappez !'));
        this.deps.floatText(key, worldText('CLOUÉE !'), PINNED_COLOUR, 1400);
      } else {
        this.deps.notice(uiText('Shefira redescend.'));
      }
    });
    room.onMessage('shefira_dive', (payload) => {
      const parsed = ShefiraDiveEventSchema.safeParse(payload);
      if (!parsed.success) return;
      const { centre, ms, target, heralded } = parsed.data;
      this.view?.dive(
        { x: centre.x * TILE_SIZE + TILE_SIZE / 2, y: centre.y * TILE_SIZE + TILE_SIZE },
        ms,
        this.now,
      );
      // Told earlier by the horn: the warning shows in the horn's gold.
      this.deps.floatText(target, worldText('PIQUÉ !'), heralded ? PINNED_COLOUR : DIVE_COLOUR, ms);
      if (target === room.sessionId) {
        cue('sfx.dragon-wings');
        this.deps.notice(uiText('Shefira plonge sur toi : écarte-toi de son ombre !'));
      } else {
        this.cueNear('sfx.dragon-wings', target);
      }
      this.deps.scene.time.delayedCall(ms, () => {
        this.deps.jolt('landing');
      });
    });
    room.onMessage('shefira_ring', (payload) => {
      const parsed = ShefiraRingEventSchema.safeParse(payload);
      if (!parsed.success) return;
      const { stage, centre, safe, ms } = parsed.data;
      if (stage === 'marked') {
        this.ring?.mark(centre, safe, ms);
        this.view?.ring(ms, this.now);
        cue('sfx.boss-telegraph');
        this.deps.notice(
          uiFormat('L’anneau de feu se resserre : reste à {rings} cases d’elle au plus !', {
            rings: safe,
          }),
        );
        return;
      }
      this.ring?.land(centre, safe);
      cue('sfx.boss-impact');
      this.deps.jolt('rumble');
    });
    room.onMessage('aerie_stations', (payload) => {
      const parsed = AerieStationsEventSchema.safeParse(payload);
      if (parsed.success) this.stations?.set(parsed.data.stations, this.now);
    });
    room.onMessage('station_used', (payload) => {
      const parsed = StationUsedEventSchema.safeParse(payload);
      if (parsed.success) this.onStationUsed(parsed.data);
    });
    room.onMessage('station_rejected', (payload) => {
      const parsed = StationRejectedSchema.safeParse(payload);
      if (!parsed.success) return;
      cue('sfx.error');
      const { stationId, reason } = parsed.data;
      const station = this.stations?.byId(stationId);
      const seconds = this.stations?.cooldownOf(stationId, this.now) ?? 0;
      this.deps.notice(
        reason === 'cooldown' && station !== undefined && seconds > 0
          ? uiFormat('{post} : prêt dans {seconds} s.', {
              post: uiText(STATION_NAMES[station.kind]),
              seconds,
            })
          : uiText(STATION_REFUSALS[reason]),
      );
    });
    room.onMessage('aerie_ally', (payload) => {
      const parsed = AerieAllyEventSchema.safeParse(payload);
      if (!parsed.success) return;
      const { mobId, role, event } = parsed.data;
      // The line as the data holds it, in the player's language (put in English at start).
      const line = STORY_ALLIES.find((ally) => ally.role === role)?.line ?? parsed.data.line;
      this.deps.viewOf(`mob:${mobId}`)?.say(line);
      this.deps.notice(
        uiFormat('{name} : « {line} »', { name: uiText(parsed.data.name), line: uiText(line) }),
      );
      if (event === 'sword') {
        this.view?.sword(this.now);
        cue('sfx.dragon-roar');
        this.deps.jolt('roar');
      }
    });
    room.onMessage('aerie_ally_act', (payload) => {
      const parsed = AerieAllyActEventSchema.safeParse(payload);
      if (!parsed.success) return;
      const { act, target } = parsed.data;
      // Méret's seal on the player; Mélisande's roots on a whelp.
      if (act === 'ward') this.deps.floatText(target, worldText('SCEAU'), SEAL_COLOUR, 900);
      else this.deps.floatText(`mob:${target}`, worldText('RACINES'), ROOTS_COLOUR, 900);
    });
    room.onMessage('aerie_ended', (payload) => {
      const parsed = AerieEndedEventSchema.safeParse(payload);
      if (!parsed.success) return;
      this.ended = true;
      this.result?.show(parsed.data);
      if (parsed.data.exitInMs === 0) return;
      if (parsed.data.outcome === 'victory') cue('sfx.loot-rare');
      if (parsed.data.outcome === 'defeat') cue('sfx.death');
    });
  }

  private onState(state: ShefiraStateEvent): void {
    const was = this.state;
    this.state = state;
    this.stateAt = this.now;
    this.view?.sync(state.airborne, this.now);
    if (was === null || was.phase === state.phase) return;
    if (state.phase === 'flight') {
      this.deps.notice(
        state.mode === 'raid'
          ? uiText('Shefira prend son envol : harponnez-la depuis deux balistes opposées !')
          : uiText('Shefira prend son envol : Nella prépare son harpon.'),
      );
    }
    if (state.phase === 'enraged') {
      cue('sfx.dragon-roar');
      this.deps.jolt('roar');
      this.deps.notice(uiText('Shefira enrage : l’anneau de feu va se resserrer autour d’elle.'));
    }
  }

  private onStationUsed(used: {
    stationId: string;
    kind: StationState['kind'];
    by?: string;
    ally?: string;
    guarded?: string[];
    pinned?: boolean;
  }): void {
    const station = this.stations?.byId(used.stationId);
    const ally = used.ally === undefined ? undefined : this.allyName(used.ally);
    const who = ally ?? (used.by === undefined ? undefined : this.deps.nameOf(used.by)) ?? '?';
    const mine = used.by === this.deps.room.sessionId;
    switch (used.kind) {
      case 'ballista': {
        if (station !== undefined)
          this.stations?.harpoon(station, this.view?.aim, used.pinned === true);
        cue('sfx.shot');
        this.deps.notice(
          used.pinned === true
            ? uiFormat('{name} plante le second harpon : Shefira est clouée !', { name: who })
            : uiFormat('{name} plante un harpon. Un second, depuis la baliste d’en face !', {
                name: who,
              }),
        );
        return;
      }
      case 'frost-brazier': {
        if (station !== undefined) this.stations?.frost(station);
        cue('sfx.spell-shield-wall');
        const veiled = used.guarded ?? [];
        for (const sessionId of veiled) {
          this.deps.floatText(sessionId, worldText('GIVRE'), FROST_COLOUR, 900);
        }
        this.deps.notice(
          veiled.includes(this.deps.room.sessionId)
            ? uiText('Le voile de givre d’Ulfar te protège.')
            : ally !== undefined
              ? uiFormat('{name} allume son brasero de givre : abrite-toi dans le voile !', {
                  name: ally,
                })
              : uiFormat('{name} allume le brasero de givre d’Ulfar.', { name: who }),
        );
        return;
      }
      case 'horn':
        cue('sfx.event-horn', 0.6);
        this.deps.notice(
          mine || ally !== undefined
            ? uiText('Le cor de Maëlle sonne : son prochain piqué sera annoncé plus tôt.')
            : uiFormat('{name} sonne le cor de Maëlle : le prochain piqué sera annoncé plus tôt.', {
                name: who,
              }),
        );
        return;
    }
  }

  /** The name of one of the story's Company, from the mob the server says worked a post. */
  private allyName(mobId: string): string | undefined {
    const kind = MobIdSchema.safeParse(this.deps.mob(mobId)?.mobId);
    return kind.success ? uiText(MOBS[kind.data].name) : undefined;
  }

  /** A shape she marked (`boss_telegraph`): on the ground, the clip that throws it. */
  telegraph(event: BossTelegraphEvent): void {
    if (event.mobId !== this.state?.mobId) return;
    this.view?.shape(event.kind, event.ms, this.now);
  }

  /** How long her flight's scene waits: she is still being struck and rising, in the arena. */
  cinematicDelay(): number {
    return this.view?.fleeLeft(this.now) ?? 0;
  }

  /** Her bar, when she is in the room: phases, flight, harpoons, the raid. */
  bar(): { mobId: string; bar: BossBarState } | null {
    const state = this.state;
    // Over: the end's banner says it, her bar goes.
    if (state === null || this.ended) return null;
    const mob = this.deps.mob(state.mobId);
    if (mob === undefined || mob.hp <= 0) return null;
    // Only her flight of phase 2 comes down by the clock; the sword's flight never does.
    const landsIn =
      state.landsInMs === null || state.phase !== 'flight'
        ? null
        : Math.max(0, Math.ceil((this.stateAt + state.landsInMs - this.now) / 1000));
    const aerie: AerieBarInfo = {
      phase: state.phase,
      airborne: state.airborne,
      landsIn,
      harpoons: state.harpoons.length,
      mode: state.mode,
      players: state.players,
    };
    return {
      mobId: state.mobId,
      bar: {
        name: MOBS.shefira.name,
        hp: mob.hp,
        maxHp: mob.maxHp,
        enraged: state.phase === 'enraged',
        aerie,
      },
    };
  }

  /**
   * What a click here means in the Aire or at its gate: her body (to strike
   * her, on the ground only), a post (walked to and used), or the crevasse
   * (walked to, then its window). Undefined when none: the scene goes on.
   */
  click(tile: TilePosition, point: Point): { attack: string } | 'handled' | undefined {
    this.visiting = undefined;
    const station = this.stations?.at(tile);
    if (station !== undefined) {
      this.visitStation(station);
      return 'handled';
    }
    const state = this.state;
    if (state !== null && this.view?.contains(point) === true) {
      const mob = this.deps.mob(state.mobId);
      if (mob !== undefined && mob.hp > 0) return { attack: state.mobId };
    }
    return undefined;
  }

  /** A click that led to the crevasse's doorway: its window opens on arrival. */
  visitGate(door: TilePosition): boolean {
    if (this.gate === undefined) return false;
    this.visiting = { kind: 'gate' };
    const me = this.deps.me();
    if (me !== undefined && this.gateDistance(me) <= AERIE_GATE_REACH) {
      this.openGate();
      return true;
    }
    return this.deps.walkTo(door);
  }

  /** Any new click on the ground: the walk to a post or the crevasse is dropped. */
  cancelVisit(): void {
    this.visiting = undefined;
  }

  /** Whether the pointer over this tile or point shows the hand: a post, or her body. */
  answers(tile: TilePosition, point: Point): boolean {
    return this.stations?.at(tile) !== undefined || this.view?.contains(point) === true;
  }

  update(): void {
    const now = this.now;
    this.stations?.update(now);
    const state = this.state;
    if (this.view !== undefined) {
      const mob = state === null ? undefined : this.deps.mob(state.mobId);
      const key = state === null ? '' : `mob:${state.mobId}`;
      this.view.update(
        now,
        mob === undefined ? undefined : this.deps.drawnAt(key),
        mob === undefined || mob.hp > 0,
        mob === undefined ? undefined : this.deps.viewOf(key),
      );
    }
    const me = this.deps.me();
    const visiting = this.visiting;
    if (me !== undefined && visiting !== undefined) {
      if (visiting.kind === 'station' && inStationReach(visiting.station, me)) {
        this.useStation(visiting.station);
      } else if (visiting.kind === 'gate' && this.gateDistance(me) <= AERIE_GATE_REACH) {
        this.openGate();
      }
    }
    // Walked away from the crevasse: its window closes.
    if (
      this.gatePanel?.isOpen === true &&
      me !== undefined &&
      this.gateDistance(me) > AERIE_GATE_REACH + 2
    ) {
      this.gatePanel.close();
    }
  }

  destroy(): void {
    this.view?.destroy();
    this.stations?.destroy();
    this.ring?.clear();
    this.result?.destroy();
    this.gatePanel?.destroy();
  }

  private visitStation(station: StationState): void {
    const me = this.deps.me();
    if (me !== undefined && inStationReach(station, me)) {
      this.useStation(station);
      return;
    }
    const approach = me === undefined ? undefined : stationApproach(station, this.deps.isOpen, me);
    if (approach === undefined || !this.deps.walkTo(approach)) {
      this.deps.notice(uiText(STATION_REFUSALS['too-far']));
      return;
    }
    this.visiting = { kind: 'station', station };
  }

  private useStation(station: StationState): void {
    this.visiting = undefined;
    this.deps.room.send('use_station', { stationId: station.id });
  }

  private openGate(): void {
    this.visiting = undefined;
    cue('sfx.open');
    this.gatePanel?.open();
    this.deps.room.send('aerie_status', {});
  }

  private enter(mode: AerieMode): void {
    this.deps.room.send('aerie_enter', { mode });
  }

  /** Rings from the crevasse's doorway to a tile, as the server measures it. */
  private gateDistance(tile: TilePosition): number {
    const gate = this.gate;
    if (gate === undefined) return Number.POSITIVE_INFINITY;
    let best = Number.POSITIVE_INFINITY;
    for (let dx = 0; dx < gate.width; dx += 1) {
      for (let dy = 0; dy < gate.height; dy += 1) {
        best = Math.min(best, ringDistance({ x: gate.x + dx, y: gate.y + dy }, tile));
      }
    }
    return best;
  }

  private cueNear(sound: string, key: string): void {
    const at = this.deps.drawnAt(key);
    if (at === undefined) cue(sound);
    else cueAt(sound, at, this.deps.listener());
  }
}
